/**
 * Input Component
 * @author Muhammad Arsan
 * @description A reusable input component using Material-UI's Input component
 */
/**
 * Input component for Form fields
 * @param {InputProps} props - The component props
 * @param {number} [props.label=SPINNER_DEFAULTS.SIZE] - The label of the field
 * @param {string} [props.iconVisibleSrc=SPINNER_DEFAULTS.COLOR] - The visible icon of the field
 * @param {number} [props.iconHiddenSrc=SPINNER_DEFAULTS.THICKNESS] - The hidden icon of the field
 * @returns {JSX.Element} The rendered Input component
 */

import React, { useRef, useEffect } from "react";
import { TextField, InputAdornment, CircularProgress } from "@mui/material";
import { FORM_PROPERTIES } from "../contants";
import type { InputFieldProps } from "../types/inputfield";
import {
	FORM_FIELD_LABEL_CLASS,
	FORM_FIELD_LABEL_CLASS_AUTH,
	FORM_INPUT_FONT_SIZE,
} from "@constants/brand.colors";
import { TextFieldStyles } from "../styles/TextField.styles";
import { AuthTextFieldStyles } from "../styles/AuthTextField.styles";

/**
 * A reusable input component for forms, built on MUI's TextField.
 * @author Muhammad Arsan
 */
const InputField: React.FC<InputFieldProps> = ({
	id,
	label,
	type = "text",
	placeholder,
	value,
	onChange,
	disabled = false,
	error = false,
	helperText = "",
	startIcon,
	endIcon,
	multiline = false,
	rows,
	className,
	labelClassName,
	inputStyleVariant = "default",
	onClick,
	isClickable,
	"aria-label": ariaLabel,
	"aria-describedby": ariaDescribedby,
	required,
	size = "medium",
	select = false,
	loading = false,
	...rest
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const selectChildren = React.Children.toArray(rest.children).filter(React.isValidElement) as React.ReactElement[];

	// Update date input colors only for date type
	useEffect(() => {
		if (type === "date" && inputRef.current) {
			const input = inputRef.current;
			if (value && value.toString().trim() !== "") {
				// Has value - dark color
				input.style.color = "#000";
				input.style.webkitTextFillColor = "#000";
			} else {
				// No value - light gray for placeholder
				input.style.color = "#6B7280";
				input.style.webkitTextFillColor = "#6B7280";
			}
		}
	}, [type, value]);

	const resolvedHelperText = helperText?.trim() ? helperText : undefined;

	return (
		<div>
			{label && (
				<label
					className={
						labelClassName ??
						(inputStyleVariant === "auth" ? FORM_FIELD_LABEL_CLASS_AUTH : FORM_FIELD_LABEL_CLASS)
					}
					htmlFor={id}>
					{label}
					{required && <span className="text-red-500">{FORM_PROPERTIES.REQUIRED_ASTERIK}</span>}
				</label>
			)}
			<TextField
				value={value}
				id={id}
				className={`${className ?? ""} ${isClickable ? "cursor-pointer" : ""}`}
				fullWidth
				variant="outlined"
				type={select ? undefined : type}
				select={select}
				placeholder={placeholder}
				onChange={onChange}
				onClick={onClick}
				disabled={disabled || loading}
				error={error}
				helperText={resolvedHelperText}
				rows={rows}
				multiline={multiline}
				aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
				aria-describedby={ariaDescribedby}
				aria-invalid={error ? "true" : "false"}
				sx={
					inputStyleVariant === "auth"
						? AuthTextFieldStyles(isClickable, size, error)
						: TextFieldStyles(isClickable, size, error)
				}
				SelectProps={{
					displayEmpty: true,
					disabled: loading,
					renderValue: (selected: unknown): React.ReactNode => {
						if (loading) {
							return (
								<div className="flex items-center space-x-2">
									<CircularProgress size={16} />
									<span style={{ color: "#9CA3AF", fontSize: FORM_INPUT_FONT_SIZE }}>Loading...</span>
								</div>
							);
						}

						if (!selected || selected === "") {
							return (
								<span style={{ color: "#9CA3AF", fontSize: FORM_INPUT_FONT_SIZE }}>{placeholder}</span>
							);
						}

						const selectedOption = selectChildren.find(
							child =>
								React.isValidElement(child) &&
								(child.props as Record<string, unknown>)?.value?.toString() === selected?.toString(),
						) as React.ReactElement | undefined;

						const children = (selectedOption?.props as Record<string, unknown>)?.children;
						if (children && typeof children === "string") {
							return (
								<span style={{ color: "#000000", fontSize: FORM_INPUT_FONT_SIZE }}>{children}</span>
							);
						}
						return (
							<span style={{ color: "#000000", fontSize: FORM_INPUT_FONT_SIZE }}>{String(selected)}</span>
						);
					},
				}}
				InputProps={{
					startAdornment: startIcon ? (
						<InputAdornment
							position="start"
							sx={{
								marginRight: -1,
								"& .MuiInputAdornment-positionStart": {
									marginRight: 0,
								},
							}}>
							{startIcon}
						</InputAdornment>
					) : undefined,
					endAdornment: endIcon ? <InputAdornment position="end">{endIcon}</InputAdornment> : undefined,
				}}
				inputRef={inputRef}
				{...rest}>
				{selectChildren}
			</TextField>
		</div>
	);
};

export default InputField;
